/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  billingGrupoListResponse,
  createBillingGrupoPayload,
  updateBillingGrupoPayload,
} from 'interfaces/BillingGrupo';
import { errorRedux } from 'interfaces/redux';
import { paramsQueryString } from 'utils/objects';
import {
  createBillingGrupo,
  deleteBillingGrupo,
  getBillingGrupoById,
  getBillingGrupoList,
  getBillingGrupoByProductCode,
  updateBillingGrupo,
} from './service';

// Action: Listar billing grupos (com filtros opcionais)
export const billingGrupoList = createAsyncThunk<
  billingGrupoListResponse,
  paramsQueryString | undefined,
  { rejectValue: errorRedux }
>('billingGrupo/list', async (params, { rejectWithValue }) => {
  try {
    const data = await getBillingGrupoList(params);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar billing grupo por código do produto
export const billingGrupoByProductCode = createAsyncThunk<
  billingGrupoListResponse,
  number,
  { rejectValue: errorRedux }
>('billingGrupo/byProductCode', async (productCode, { rejectWithValue }) => {
  try {
    const data = await getBillingGrupoByProductCode(productCode);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar billing grupo por ID
export const billingGrupoById = createAsyncThunk<
  billingGrupoListResponse,
  number,
  { rejectValue: errorRedux }
>('billingGrupo/getById', async (id, { rejectWithValue }) => {
  try {
    const data = await getBillingGrupoById(id);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Criar billing grupo
export const billingGrupoCreate = createAsyncThunk<
  billingGrupoListResponse,
  createBillingGrupoPayload,
  { rejectValue: errorRedux }
>('billingGrupo/create', async (payload, { rejectWithValue }) => {
  try {
    const data = await createBillingGrupo(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Atualizar billing grupo
export const billingGrupoUpdate = createAsyncThunk<
  billingGrupoListResponse,
  updateBillingGrupoPayload,
  { rejectValue: errorRedux }
>('billingGrupo/update', async (payload, { rejectWithValue }) => {
  try {
    const data = await updateBillingGrupo(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Deletar billing grupo
export const billingGrupoDelete = createAsyncThunk<void, number, { rejectValue: errorRedux }>(
  'billingGrupo/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deleteBillingGrupo(id);
      return undefined;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Reducer
interface IBillingGrupo extends errorRedux {
  billingGrupoList: billingGrupoListResponse;
  billingGrupoById: billingGrupoListResponse;
}

const initialState: IBillingGrupo = {
  status: 'idle',
  message: '',
  billingGrupoList: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  billingGrupoById: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
};

export const BillingGrupoReducer = createSlice({
  name: 'BillingGrupoStatus',
  initialState,
  reducers: {
    clearBillingGrupoStatus: (state) => {
      state.status = 'idle';
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(billingGrupoList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(billingGrupoList.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.billingGrupoList = action.payload;
      })
      .addCase(billingGrupoList.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(billingGrupoById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(billingGrupoById.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.billingGrupoById = action.payload;
      })
      .addCase(billingGrupoById.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(billingGrupoCreate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(billingGrupoCreate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(billingGrupoCreate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(billingGrupoUpdate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(billingGrupoUpdate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(billingGrupoUpdate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(billingGrupoDelete.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(billingGrupoDelete.fulfilled, (state) => {
        state.status = 'completed';
        state.message = 'Grupo de Cobrança deletado com sucesso';
      })
      .addCase(billingGrupoDelete.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(billingGrupoByProductCode.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(billingGrupoByProductCode.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.billingGrupoList = action.payload;
      })
      .addCase(billingGrupoByProductCode.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      });
  },
});

export const { clearBillingGrupoStatus } = BillingGrupoReducer.actions;
export default BillingGrupoReducer.reducer;
